import React, { useState, useEffect, useRef, useCallback } from 'react';
import { search, trending, selectedContent } from '../../api/api';
import SearchBar from '../../components/SearchBar/SearchBar';
import ImageCard from '../../components/ImageCard/ImageCard';
import SelectedImagePanel from './components/SelectedImagePanel';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './explore.css';

const categories = [
  { label: 'All', color: 'default' },
  { label: 'Anime', color: 'primary' },
  { label: 'Digital Art', color: 'secondary' },
  { label: 'Photography', color: 'success' },
  { label: 'AI Generated', color: 'error' },
  { label: 'Abstract', color: 'warning' },
  { label: 'Nature', color: 'info' },
  { label: 'Illustration', color: 'default' },
  { label: 'Character Design', color: 'primary' },
  { label: 'Concept Art', color: 'secondary' },
  { label: 'Fan Art', color: 'success' },
  { label: '3D Art', color: 'error' },
];

const ExplorePage = () => {
  const [images, setImages] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [showImagePanel, setShowImagePanel] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const ITEMS_PER_PAGE = 50;
  const SCROLL_THRESHOLD = 0.5;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef(null);
  const observer = useRef();
  const loadingRef = useRef(null);
  const [debug, setDebug] = useState({
    observing: false,
    lastNode: null
  });
  const [columnCount, setColumnCount] = useState(5);

  // SEO metadata
  const getPageMetadata = () => {
    const query = searchParams.get('q');
    const category = searchParams.get('category');
    
    return {
      title: query 
        ? `${query} - InspiraPix Search Results` 
        : category 
          ? `${category} Images - InspiraPix`
          : 'Explore AI Generated Art - InspiraPix',
      description: query
        ? `Explore AI-generated images of ${query} on InspiraPix. Find inspiration and discover amazing artworks.`
        : 'Discover and explore AI-generated art, digital illustrations, and creative images on InspiraPix.',
      keywords: `AI art, ${query || 'digital art'}, ${category || 'art generation'}, InspiraPix, creative images`
    };
  };

  const lastImageElementRef = useCallback(node => {
    if (loading || !hasMore) return;

    if (observer.current) {
      observer.current.disconnect();
    }
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        const query = searchParams.get('q');
        const category = searchParams.get('category');
        const generatedBy = searchParams.get('generatedBy');
        
        if (query) {
          performSearch({
            query: query.toLowerCase(),
            category: category || 'All',
            page: page + 1,
            sortBy: searchParams.get('sortBy') || 'relevance',
            timeRange: searchParams.get('timeRange') || 'all',
            aspectRatio: searchParams.get('aspectRatio') || 'all',
            generatedBy,
            append: true
          });
        } else {
          fetchTrendingImages(true);
        }
        
        setPage(prev => prev + 1);
      }
    }, {
      root: null,
      threshold: SCROLL_THRESHOLD,
      rootMargin: '500px'
    });

    if (node) {
      observer.current.observe(node);
    }
  }, [loading, hasMore, searchParams, page]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsAuthenticated(!!token);
  }, []);

  useEffect(() => {
    const initializeExplore = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const imageIdFromUrl = searchParams.get('selected');
        const query = searchParams.get('q');
        const category = searchParams.get('category');
        const generatedBy = searchParams.get('generatedBy');
        
        // Reset state
        setImages([]);
        setPage(1);
        setHasMore(true);
        
        if (category) {
          setSelectedCategory(category);
        }
        
        if (imageIdFromUrl) {
          await fetchImageDetails(imageIdFromUrl);
        } else if (query) {
          await performSearch({
            query: query.toLowerCase(),
            category: category || 'All',
            page: 1,
            sortBy: searchParams.get('sortBy') || 'relevance',
            timeRange: searchParams.get('timeRange') || 'all',
            aspectRatio: searchParams.get('aspectRatio') || 'all',
            generatedBy
          });
        } else if (generatedBy) {
          await performSearch({
            query: '',
            page: 1,
            generatedBy
          });
        } else {
          await fetchTrendingImages();
        }
      } catch (err) {
        setError(err.message || 'Failed to load images. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    initializeExplore();
  }, [searchParams]);

  useEffect(() => {
    const category = searchParams.get('category');
    if (category) {
      setSelectedCategory(category);
    }
  }, [searchParams]);

  const fetchImageDetails = async (id) => {
    try {
      setLoading(true);
      const response = await selectedContent(id);
      const imageData = response.data.item;
      setSelectedImage(imageData);
      setShowImagePanel(true);
      
      if (imageData) {
        const searchTerms = [
          imageData.prompt,
          ...(imageData.tags || [])
        ].filter(Boolean).join(' ');

        if (searchTerms) {
          // Reset pagination states when showing related images
          setPage(1);
          setHasMore(true);
          
          // Update URL with the search terms for related images
          const params = new URLSearchParams(searchParams);
          params.set('selected', id);
          params.set('related', searchTerms);
          params.set('page', '1');
          setSearchParams(params);

          await performSearch({
            query: searchTerms,
            category: 'All',
            page: 1,
            exclude: [imageData.publicId]
          });
        }
      }
    } catch (error) {
      console.error('Error fetching image details:', error);
      setShowImagePanel(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrendingImages = async (append = false) => {
    try {
      setLoading(true);
      const response = await trending({
        params: {
          limit: ITEMS_PER_PAGE,
          skip: (page - 1) * ITEMS_PER_PAGE
        }
      });
      
      const newImages = response.data.results.items || [];
      
      setImages(prev => {
        if (!append) return newImages;
        
        // Create a Set of existing publicIds to prevent duplicates
        const existingIds = new Set(prev.map(img => img.publicId));
        const uniqueNewImages = newImages.filter(img => !existingIds.has(img.publicId));
        
        return [...prev, ...uniqueNewImages];
      });
      
      setHasMore(newImages.length >= ITEMS_PER_PAGE);
    } catch (error) {
      console.error('Error fetching trending images:', error);
      if (!append) {
        setImages([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const performSearch = async ({ query, category, page: searchPage = 1, exclude, append = false, generatedBy, ...filters }) => {
    try {
      setLoading(true);
      const response = await search({
        params: {
          input: query,
          category: category !== 'All' ? category : undefined,
          limit: ITEMS_PER_PAGE,
          skip: (searchPage - 1) * ITEMS_PER_PAGE,
          exclude: exclude || (selectedImage ? [selectedImage.publicId] : undefined),
          generatedBy,
          ...filters,
        },
      });
      
      if (response.data && response.data.results) {
        const newImages = response.data.results.items || [];
        
        setImages(prev => {
          if (!append) return newImages;
          
          // Create a Set of existing publicIds to prevent duplicates
          const existingIds = new Set(prev.map(img => img.publicId));
          const uniqueNewImages = newImages.filter(img => !existingIds.has(img.publicId));
          
          return [...prev, ...uniqueNewImages];
        });
        
        setHasMore(newImages.length >= ITEMS_PER_PAGE);
      } else {
        if (!append) {
          setImages([]);
        }
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error searching images:', error);
      if (!append) {
        setImages([]);
      }
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = ({ query, category, generatedBy, ...filters }) => {
    setShowImagePanel(false);  // Close the image panel when searching
    setSelectedImage(null);    // Clear selected image
    setImages([]);            // Clear existing images
    setPage(1);              // Reset page number
    
    // Update URL parameters
    const searchParams = new URLSearchParams();
    if (query) searchParams.set('q', query);
    if (category && category !== 'All') searchParams.set('category', category);
    if (generatedBy) searchParams.set('generatedBy', generatedBy);
    Object.entries(filters).forEach(([key, value]) => {
      if (value) searchParams.set(key, value);
    });
    searchParams.set('page', '1');
    
    // Update URL without reloading the page
    navigate({
      pathname: '/explore',
      search: searchParams.toString()
    });

    // Perform the search
    performSearch({ query, category, page: 1, generatedBy, ...filters });
  };

  const handleImageClick = async (image) => {
    if (!image || !image.publicId) {
      console.error('Invalid image data:', image);
      return;
    }
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.set('selected', image.publicId);
    window.location.href = `/explore?${currentParams.toString()}`;
  };

  const handleCloseDetail = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('selected');
    params.delete('related'); // Also remove related search terms
    setSearchParams(params);
    setSelectedImage(null);
    setShowImagePanel(false);
    
    // Reset pagination states
    setPage(1);
    setHasMore(true);
    
    // Reload the previous search or trending
    const query = params.get('q');
    if (query) {
      performSearch({
        query: query.toLowerCase(),
        category: params.get('category') || 'All',
        page: 1,
        sortBy: params.get('sortBy') || 'relevance',
        timeRange: params.get('timeRange') || 'all',
        aspectRatio: params.get('aspectRatio') || 'all'
      });
    } else {
      fetchTrendingImages();
    }
  };

  const calculateSpans = (imageElement) => {
    const height = imageElement.offsetHeight;
    const spans = Math.ceil(height / 10);
    imageElement.parentElement.style.setProperty('--span', spans);
  };

  const handleImageLoad = (event) => {
    const img = event.target;
    const item = img.closest('.inspirapix-explore-masonry-item');
    
    // Just remove loading state
    item.style.opacity = '1';
  };

  // Add this effect to help debug state changes
  useEffect(() => {
    // console.log('hasMore changed:', hasMore);
  }, [hasMore]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setPage(1);
    setImages([]);
    
    // Update URL parameters
    const params = new URLSearchParams(searchParams);
    if (category !== 'All') {
      params.set('category', category);
    } else {
      params.delete('category');
    }
    params.set('page', '1');
    
    // Update URL without reloading the page
    navigate({
      pathname: '/explore',
      search: params.toString()
    });

    // Perform search with new category
    const query = params.get('q');
    performSearch({
      query: query || '',
      category,
      page: 1,
      sortBy: params.get('sortBy') || 'relevance',
      timeRange: params.get('timeRange') || 'all',
      aspectRatio: params.get('aspectRatio') || 'all'
    });
  };

  // Add window resize handler
  useEffect(() => {
    const updateColumnCount = () => {
      const width = window.innerWidth;
      if (width > 1600) setColumnCount(5);
      else if (width > 1200) setColumnCount(4);
      else if (width > 768) setColumnCount(3);
      else if (width > 480) setColumnCount(2);
      else setColumnCount(1);
    };

    updateColumnCount();
    window.addEventListener('resize', updateColumnCount);
    return () => window.removeEventListener('resize', updateColumnCount);
  }, []);

  // Distribute images into columns
  const getColumnsData = useCallback(() => {
    const columns = Array.from({ length: columnCount }, () => []);
    
    images.forEach((image, index) => {
      const columnIndex = index % columnCount;
      columns[columnIndex].push(image);
    });
    
    return columns;
  }, [images, columnCount]);

  return (
    <>
      <Helmet>
        <title>{getPageMetadata().title}</title>
        <meta name="description" content={getPageMetadata().description} />
        <meta name="keywords" content={getPageMetadata().keywords} />
      </Helmet>
      <div className="inspirapix-explore-page">
        <div className="inspirapix-explore-wrapper">
          <div className="inspirapix-explore-controls">
            <SearchBar
              onSearch={handleSearch}
              showFilters={true}
              showCategories={true}
              categories={categories}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              initialQuery={searchParams.get('q') || ''}
            />
          </div>

          <div className="inspirapix-explore-content">
            {showImagePanel && selectedImage && (
              <SelectedImagePanel
                selectedImage={selectedImage}
                isAuthenticated={isAuthenticated}
                onClose={handleCloseDetail}
                onSearch={handleSearch}
              />
            )}

            {error && (
              <div className="inspirapix-explore-error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-button">
                  Try Again
                </button>
              </div>
            )}

            {!error && (
              <div className="inspirapix-explore-masonry-grid" ref={gridRef}>
                {getColumnsData().map((column, columnIndex) => (
                  <div key={`column-${columnIndex}`} className="inspirapix-explore-masonry-column">
                    {column.map((image, imageIndex) => {
                      const isLastImage = columnIndex === columnCount - 1 && 
                                       imageIndex === column.length - 1 && 
                                       images.length >= ITEMS_PER_PAGE;
                      
                      return (
                        <div 
                          key={`${image.publicId}`}
                          className="inspirapix-explore-masonry-item"
                          onClick={() => handleImageClick(image)}
                          ref={isLastImage ? lastImageElementRef : null}
                        >
                          <img 
                            src={image.path}
                            alt={image.prompt}
                            onLoad={handleImageLoad}
                            loading="lazy"
                          />
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}

            {loading && (
              <div className="inspirapix-explore-loading" ref={loadingRef}>
                Loading more images...
              </div>
            )}

            {!loading && !error && images.length === 0 && (
              <div className="inspirapix-explore-no-images">
                No images found. Try adjusting your search criteria.
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ExplorePage;
